// PDF de la receta común a firmar — Fase 5, sección 2.8, ampliado 2026-09-02
// (varios medicamentos por receta + campos que exige AM 1124 Art. 7 que
// faltaban). Desde 2026-09-12 lo arma el SERVIDOR (POST
// /api/hce/firmar-documento) a partir de la especificación que manda
// hce-chart, y le agrega el sello de Security Data (o el simulado).
//
// [2026-09-15] El QR de "validación de dispensación" (portal público
// `/farmacia/:token`) se sacó del PDF a pedido explícito de Sue — el token y
// el cálculo de `payloadHash` siguen existiendo server-side (los sigue
// usando la cola de farmacia interna), solo dejó de imprimirse en el papel.
//
// Rediseño visual 2026-09-02 (handoff del usuario: "Rediseño de receta
// médica.pdf" + prompt de especificación) — tamaño carta (antes A4),
// tipografía Newsreader/IBM Plex Sans (antes Helvetica estándar), paleta
// navy/gris azulado con acentos rojos para alertas, tarjetas de medicamento
// con grilla de 3 columnas, sección "Indicaciones" con callout de signos de
// alarma. Colores convertidos de OKLCH a sRGB con `culori` (ver commit) —
// pdf-lib no soporta oklch nativo. Simplificación deliberada frente al
// mockup: sin `border-radius` (pdf-lib no tiene esquinas redondeadas nativas
// y el bezier a mano no valía el riesgo para un detalle tan chico) — todo
// rectángulo va con esquina recta.
import { PDFDocument, rgb, type PDFFont, type PDFPage } from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';
import type { FuentesPdf } from './fuentes';
import { lineaContactoConsultorio } from './datosConsultorioHeader';

// Paleta — valores OKLCH del handoff convertidos a sRGB (culori, formatHex),
// ver el comentario de cabecera. Nombres en español, mismo criterio que el
// resto del archivo.
const AZUL = rgb(0, 0x39 / 255, 0x57 / 255); // oklch(0.32 0.09 230) — títulos, N° de receta
const LINEA_HEADER = rgb(0, 0x4f / 255, 0x6a / 255); // oklch(0.4 0.08 230) — regla bajo el header
const TEXTO = rgb(0x16 / 255, 0x1c / 255, 0x1e / 255); // oklch(0.22 0.01 230) — texto principal
const GRIS_OSCURO = rgb(0x3d / 255, 0x4a / 255, 0x51 / 255); // oklch(0.4 0.02 230) — etiquetas/meta más oscuras
const GRIS_CLARO = rgb(0x6c / 255, 0x73 / 255, 0x76 / 255); // oklch(0.55 0.01 230) — etiquetas/meta más claras
const FONDO_BLOQUE = rgb(0xf1 / 255, 0xf6 / 255, 0xf8 / 255); // oklch(0.97 0.006 230) — fondo paciente/alergias
const ROJO_TEXTO = rgb(0x9b / 255, 0x1e / 255, 0x22 / 255); // oklch(0.45 0.16 25) — alergias/signos de alarma
const ROJO_FONDO = rgb(0xff / 255, 0xf0 / 255, 0xee / 255); // oklch(0.97 0.02 25) — callout signos de alarma
const ROJO_BORDE = rgb(0xbd / 255, 0x41 / 255, 0x3f / 255); // oklch(0.55 0.16 25) — borde izquierdo del callout
const BORDE_SUTIL = rgb(0xc7 / 255, 0xcf / 255, 0xd3 / 255); // oklch(0.85 0.01 230) — bordes de tarjeta/pie
const FONDO_PAGINA = rgb(0xfc / 255, 0xfe / 255, 0xfe / 255); // oklch(0.995 0.002 230) — blanco cálido casi puro

// Carta (8.5×11in) — el mockup lo pide explícito, antes A4.
const PAGE_W = 612;
const PAGE_H = 792;
const MARGIN_X = 54; // 0.75in
// 0.65in → 0.5in: pedido del usuario para que entren al menos 3
// medicamentos + contacto + firma en una sola hoja — el margen es el
// espacio "gratis" más fácil de recortar antes de tocar tamaños de letra.
const MARGIN_Y = 0.5 * 72; // 0.5in
const CONTENT_W = PAGE_W - MARGIN_X * 2;

function wrapText(text: string, font: PDFFont, size: number, maxWidth: number): string[] {
  const palabras = text.split(/\s+/);
  const lineas: string[] = [];
  let actual = '';
  for (const palabra of palabras) {
    const intento = actual ? `${actual} ${palabra}` : palabra;
    if (font.widthOfTextAtSize(intento, size) > maxWidth && actual) {
      lineas.push(actual);
      actual = palabra;
    } else {
      actual = intento;
    }
  }
  if (actual) lineas.push(actual);
  return lineas;
}

/** dd/mm/aaaa — Resolución ACESS-2023-0030, Art. 5.a.ii fija este formato exacto para la fecha impresa. */
function fechaDdMmAaaa(fechaISO: string): string {
  // Hora de Ecuador explícita: desde el 2026-09-12 esto corre en el servidor
  // (UTC) — con getDate()/getMonth() una receta firmada después de las 19:00
  // saldría fechada al día siguiente.
  const partes = new Intl.DateTimeFormat('es-EC', { timeZone: 'America/Guayaquil', day: '2-digit', month: '2-digit', year: 'numeric' }).formatToParts(new Date(fechaISO));
  const parte = (tipo: Intl.DateTimeFormatPartTypes): string => partes.find((p) => p.type === tipo)?.value ?? '';
  return `${parte('day')}/${parte('month')}/${parte('year')}`;
}

export interface MedicamentoPdfReceta {
  nombreMedicamento: string;
  /** AM 1124 Art. 7.II.10 / Resolución ACESS-2023-0030 Art. 5.c: el nombre comercial se puede añadir de forma opcional/secundaria al DCI — si el médico lo carga, tiene que aparecer como su propio campo, no solo mezclado dentro de `nombreMedicamento`. */
  nombreComercial?: string;
  /** "Oral", "Intravenosa"... — Resolución ACESS-2023-0030 Art. 5.c.iv, campo propio (no se infiere de la forma farmacéutica). */
  viaAdministracion?: string;
  pauta?: string;
  cantidadTotal?: string;
  /** "treinta" — Art. 5.c.v exige la cantidad en números Y letras. */
  cantidadEnLetras?: string;
}

export interface DatosPdfReceta {
  /** Correlativo del consultorio ("Receta digital", checklist MSP/ACESS — `lib/receta-numeracion.ts`, ya no un identificador aleatorio). */
  numeroReceta: string;
  /** Nombre del consultorio — "Razón social" si el consultorio la cargó (Datos del consultorio), si no el nombre comercial (`clinicName`). */
  consultorioNombre?: string;
  /** Ciudad de emisión — Resolución ACESS-2023-0030 Art. 5.a.ii. En multiples-sedes, la de la sede de la especialidad del médico; si no, la cargada en Datos del consultorio. */
  ciudadEmision?: string;
  /** RUC del establecimiento — deliberadamente separado del RUC bancario (`bank.ruc`). */
  ruc?: string;
  /** Dirección/teléfono/correo del consultorio (pedido de Sue, 2026-09-14) — se dibujan como una sola línea bajo el RUC, ver `lineaContactoConsultorio`. */
  direccionConsultorio?: string;
  telefonoConsultorio?: string;
  correoConsultorio?: string;
  /** Texto libre cargado por el médico al firmar, ej. "30 días desde la fecha de emisión" — Art. 5.a.iv no fija un valor único razonable para todo tratamiento. */
  vigencia?: string;
  pacienteNombre: string;
  pacienteIdentificador?: string;
  /** "34 años, 5 meses" — Art. 5.b.ii. */
  pacienteEdad?: string;
  /** "Masculino" | "Femenino" — dato del paciente, no exigido explícitamente por Art. 5.b pero ya se venía imprimiendo. */
  pacienteSexo?: string;
  /** Antecedentes de alergias — Art. 5.b.iv, traído automáticamente de Medplum (AllergyIntolerance activas) o "No refiere" explícito, nunca ausente. */
  alergias?: string;
  /** CIE-10 presuntivo — Art. 5.b.iii. */
  diagnosticoPresuntivo?: string;
  practitionerNombre: string;
  practitionerRegistro: string;
  /** Número de contacto permanente del prescriptor — Resolución ACESS-2023-0030, Art. 5.e.vi (sección "Indicaciones"). */
  practitionerTelefono?: string;
  fechaISO: string;
  /** Uno o más — AM 1124 Art. 5 permite "uno o más medicamentos... en la receta médica" con una sola firma. */
  medicamentos: MedicamentoPdfReceta[];
  sustanciaControlada: boolean;
  numeroSerieRecetaEspecial?: string;
  /**
   * Sección "Indicaciones" (Resolución ACESS-2023-0030, Art. 5.e) — texto
   * libre, opcional en el wizard, el médico decide si aplica para esta
   * receta. `practitionerTelefono` (arriba) va en el pie, no acá.
   */
  signosAlarma?: string;
  recomendacionesNoFarmacologicas?: string;
  /** URL completa del portal de validación (`${vaultUrl}/farmacia/${token}`) — omitida en receta especial, que usa el QR físico de ACESS, no el propio. */
  urlValidacion?: string;
}

interface Fuentes {
  serifBold: PDFFont;
  sans: PDFFont;
  sansMedium: PDFFont;
  sansBold: PDFFont;
}

async function cargarFuentes(doc: PDFDocument, fuentes: FuentesPdf): Promise<Fuentes> {
  doc.registerFontkit(fontkit);
  const [serifBold, sans, sansMedium, sansBold] = await Promise.all([
    doc.embedFont(fuentes.serifSemiBold),
    doc.embedFont(fuentes.sansRegular),
    doc.embedFont(fuentes.sansMedium),
    doc.embedFont(fuentes.sansSemiBold),
  ]);
  return { serifBold, sans, sansMedium, sansBold };
}

/** Uppercase con tracking manual (letter-spacing) — pdf-lib no soporta letter-spacing por `drawText`, así que se dibuja carácter por carácter avanzando el cursor. Devuelve el ancho total dibujado. */
function drawTracked(page: PDFPage, texto: string, x: number, y: number, font: PDFFont, size: number, color: ReturnType<typeof rgb>, tracking = 0.6): number {
  let cx = x;
  for (const ch of texto) {
    page.drawText(ch, { x: cx, y, size, font, color });
    cx += font.widthOfTextAtSize(ch, size) + tracking;
  }
  return cx - x;
}

export async function generarPdfReceta(datos: DatosPdfReceta, fuentes: FuentesPdf): Promise<Uint8Array> {
  const doc = await PDFDocument.create();
  const f = await cargarFuentes(doc, fuentes);

  let page = doc.addPage([PAGE_W, PAGE_H]);
  page.drawRectangle({ x: 0, y: 0, width: PAGE_W, height: PAGE_H, color: FONDO_PAGINA });
  let y = PAGE_H - MARGIN_Y;

  function lineH(size: number): number {
    return size * 1.32;
  }

  /** Nueva página en blanco (mismo fondo), sin repetir el header — pedido explícito del handoff: para el volumen real de medicamentos de una receta no vale la pena repetirlo. */
  function nuevaPagina(): void {
    page = doc.addPage([PAGE_W, PAGE_H]);
    page.drawRectangle({ x: 0, y: 0, width: PAGE_W, height: PAGE_H, color: FONDO_PAGINA });
    y = PAGE_H - MARGIN_Y;
  }

  /** Si lo que sigue no entra por encima de `pisoY`, arranca página nueva. */
  function asegurarEspacio(alturaNecesaria: number, pisoY: number): void {
    if (y - alturaNecesaria < pisoY) nuevaPagina();
  }

  function parrafo(
    texto: string,
    x: number,
    maxWidth: number,
    opts: { font?: PDFFont; size?: number; color?: ReturnType<typeof rgb>; lineGap?: number } = {},
  ): void {
    const font = opts.font ?? f.sans;
    const size = opts.size ?? 10.5;
    for (const linea of wrapText(texto, font, size, maxWidth)) {
      page.drawText(linea, { x, y, size, font, color: opts.color ?? TEXTO });
      y -= opts.lineGap ?? lineH(size);
    }
  }

  function anchoTexto(texto: string, font: PDFFont, size: number): number {
    return font.widthOfTextAtSize(texto, size);
  }

  // ---------------------------------------------------------------------
  // 1. HEADER
  // ---------------------------------------------------------------------
  const yHeaderTop = y;
  // Izquierda: título + clínica + RUC.
  page.drawText(datos.sustanciaControlada ? 'Receta especial' : 'Receta médica', {
    x: MARGIN_X,
    y: yHeaderTop - 17,
    size: 20,
    font: f.serifBold,
    color: AZUL,
  });
  let yIzq = yHeaderTop - 17 - lineH(20) + 5;
  if (datos.consultorioNombre) {
    page.drawText(datos.consultorioNombre, { x: MARGIN_X, y: yIzq, size: 11, font: f.sans, color: GRIS_OSCURO });
    yIzq -= lineH(11);
  }
  if (datos.ruc) {
    page.drawText(`RUC: ${datos.ruc}`, { x: MARGIN_X, y: yIzq, size: 10, font: f.sans, color: GRIS_CLARO });
    yIzq -= lineH(10);
  }
  const contactoConsultorio = lineaContactoConsultorio({
    direccion: datos.direccionConsultorio,
    telefono: datos.telefonoConsultorio,
    correo: datos.correoConsultorio,
  });
  if (contactoConsultorio) {
    page.drawText(contactoConsultorio, { x: MARGIN_X, y: yIzq, size: 8.5, font: f.sans, color: GRIS_CLARO });
    yIzq -= lineH(8.5);
  }

  // Derecha: N° de receta / fecha / vigencia, alineados al margen derecho.
  let yDer = yHeaderTop;
  const numeroTexto = `N° de receta: ${datos.numeroReceta}`;
  page.drawText(numeroTexto, {
    x: PAGE_W - MARGIN_X - anchoTexto(numeroTexto, f.sansBold, 11.5),
    y: yDer,
    size: 11.5,
    font: f.sansBold,
    color: AZUL,
  });
  yDer -= lineH(11.5) + 2;
  const ciudadFecha = [datos.ciudadEmision, fechaDdMmAaaa(datos.fechaISO)].filter(Boolean).join(', ');
  const fechaTexto = `Fecha: ${ciudadFecha}`;
  page.drawText(fechaTexto, { x: PAGE_W - MARGIN_X - anchoTexto(fechaTexto, f.sans, 10.5), y: yDer, size: 10.5, font: f.sans, color: TEXTO });
  yDer -= lineH(10.5);
  if (datos.vigencia) {
    const vigTexto = `Vigencia: ${datos.vigencia}`;
    page.drawText(vigTexto, { x: PAGE_W - MARGIN_X - anchoTexto(vigTexto, f.sans, 10.5), y: yDer, size: 10.5, font: f.sans, color: GRIS_CLARO });
    yDer -= lineH(10.5);
  }

  y = Math.min(yIzq, yDer) - 10; // padding-bottom antes de la regla.
  page.drawLine({ start: { x: MARGIN_X, y }, end: { x: PAGE_W - MARGIN_X, y }, thickness: 2, color: LINEA_HEADER });
  y -= 15;

  // ---------------------------------------------------------------------
  // 2. BLOQUE PACIENTE / ALERGIAS (fondo gris azulado, grid 1.4fr/1fr)
  // ---------------------------------------------------------------------
  const gapCols = 20;
  const col1W = (CONTENT_W - gapCols) * (1.4 / 2.4);
  const col2X = MARGIN_X + col1W + gapCols;
  const col2W = CONTENT_W - col1W - gapCols;
  const padBloque = { x: 14, yTop: 10, yBottom: 10 };

  const pacienteLinea1 = `${datos.pacienteNombre}${datos.pacienteIdentificador ? ` (${datos.pacienteIdentificador})` : ''}`;
  const datosPaciente = [datos.pacienteEdad, datos.pacienteSexo].filter(Boolean).join(' · ');
  const lineasPaciente = wrapText(pacienteLinea1, f.sansBold, 13, col1W);
  const alturaBloque =
    padBloque.yTop +
    lineH(9.5) + // etiqueta
    lineasPaciente.length * lineH(13) +
    (datosPaciente ? lineH(11) : 0) +
    padBloque.yBottom;

  asegurarEspacio(alturaBloque + 16, MARGIN_Y);
  const yBloqueTop = y;
  page.drawRectangle({ x: MARGIN_X, y: yBloqueTop - alturaBloque, width: CONTENT_W, height: alturaBloque, color: FONDO_BLOQUE });

  let yCol1 = yBloqueTop - padBloque.yTop - 7;
  drawTracked(page, 'PACIENTE', MARGIN_X + padBloque.x, yCol1, f.sans, 9.5, GRIS_OSCURO);
  yCol1 -= lineH(9.5) + 2;
  for (const linea of lineasPaciente) {
    page.drawText(linea, { x: MARGIN_X + padBloque.x, y: yCol1, size: 13, font: f.sansBold, color: TEXTO });
    yCol1 -= lineH(13);
  }
  if (datosPaciente) {
    page.drawText(datosPaciente, { x: MARGIN_X + padBloque.x, y: yCol1, size: 11, font: f.sans, color: GRIS_CLARO });
  }

  let yCol2 = yBloqueTop - padBloque.yTop - 7;
  drawTracked(page, 'ALERGIAS', col2X + padBloque.x, yCol2, f.sans, 9.5, GRIS_OSCURO);
  yCol2 -= lineH(9.5) + 2;
  // "No refiere" (sin alergias reales) se pinta neutro, no en rojo de alerta
  // — el rojo es para cuando SÍ hay algo que el farmacéutico deba notar.
  const hayAlergiaReal = !!datos.alergias && datos.alergias.trim().toLowerCase() !== 'no refiere';
  for (const linea of wrapText(datos.alergias || 'No refiere', f.sansBold, 12, col2W - padBloque.x)) {
    page.drawText(linea, { x: col2X + padBloque.x, y: yCol2, size: 12, font: f.sansBold, color: hayAlergiaReal ? ROJO_TEXTO : GRIS_CLARO });
    yCol2 -= lineH(12);
  }

  y = yBloqueTop - alturaBloque - 14;

  // ---------------------------------------------------------------------
  // 3. DIAGNÓSTICO / PRESCRIPTOR (sin fondo, mismo grid)
  // ---------------------------------------------------------------------
  asegurarEspacio(55, MARGIN_Y);
  const yFilaTop = y;
  let yD = yFilaTop;
  if (datos.diagnosticoPresuntivo) {
    drawTracked(page, 'DIAGNÓSTICO PRESUNTIVO', MARGIN_X, yD, f.sans, 9.5, GRIS_OSCURO);
    yD -= lineH(9.5) + 2;
    for (const linea of wrapText(datos.diagnosticoPresuntivo, f.sans, 12, col1W)) {
      page.drawText(linea, { x: MARGIN_X, y: yD, size: 12, font: f.sans, color: TEXTO });
      yD -= lineH(12);
    }
  }
  let yP = yFilaTop;
  drawTracked(page, 'PRESCRIPTOR', col2X, yP, f.sans, 9.5, GRIS_OSCURO);
  yP -= lineH(9.5) + 2;
  for (const linea of wrapText(`${datos.practitionerNombre} — Registro MSP/ACESS ${datos.practitionerRegistro}`, f.sans, 12, col2W)) {
    page.drawText(linea, { x: col2X, y: yP, size: 12, font: f.sans, color: TEXTO });
    yP -= lineH(12);
  }
  y = Math.min(yD, yP) - 14;

  // ---------------------------------------------------------------------
  // 4. TARJETAS DE MEDICAMENTO — borde 1px, grid interno de 3 columnas
  // ---------------------------------------------------------------------
  const cardPad = { x: 14, y: 10 };
  const cardGapCols = 14;
  const cardColW = (CONTENT_W - cardPad.x * 2 - cardGapCols * 2) / 3;
  // 16→13 (pedido original, ahorrar espacio) →11 (pedido posterior: que
  // entren al menos 3 medicamentos + QR + contacto + firma en una sola
  // hoja) — el nombre completo del catálogo ARCSA (DCI + forma) puede
  // ocupar 2 líneas incluso a este tamaño; sigue siendo la jerarquía visual
  // más alta de la tarjeta (serif bold azul vs. sans gris del resto).
  const TAMANO_TITULO_MEDICAMENTO = 11;

  datos.medicamentos.forEach((m, i) => {
    const tituloTexto = `${i + 1}. ${m.nombreMedicamento}`.toUpperCase();
    const lineasTitulo = wrapText(tituloTexto, f.serifBold, TAMANO_TITULO_MEDICAMENTO, CONTENT_W - cardPad.x * 2);
    const lineasComercial = m.nombreComercial ? wrapText(`Nombre comercial: ${m.nombreComercial}`, f.sans, 10, CONTENT_W - cardPad.x * 2) : [];

    const columnas: [string, string | undefined][] = [
      ['VÍA DE ADMINISTRACIÓN', m.viaAdministracion],
      ['PAUTA', m.pauta],
      ['CANTIDAD TOTAL', m.cantidadTotal ? `${m.cantidadTotal}${m.cantidadEnLetras ? ` (${m.cantidadEnLetras})` : ''}` : undefined],
    ];
    // Cada columna puede envolver a más de una línea (ej. "1 tableta cada 8
    // horas por 5 días" no siempre entra en 1/3 del ancho de la tarjeta) — la
    // altura de la fila de 3 columnas la marca la que más líneas necesite,
    // nunca una altura fija de 1 línea (bug real: antes todas las líneas de
    // un valor se dibujaban en el mismo Y, superpuestas — verificado
    // extrayendo el texto del PDF real, salía todo pegado).
    const lineasPorColumna = columnas.map(([, valor]) => (valor ? wrapText(valor, f.sans, 11, cardColW) : []));
    const maxLineasValor = Math.max(1, ...lineasPorColumna.map((l) => l.length));

    const alturaCard =
      cardPad.y * 2 +
      lineasTitulo.length * lineH(TAMANO_TITULO_MEDICAMENTO) +
      (lineasComercial.length ? lineasComercial.length * lineH(10) + 3 : 0) +
      6 +
      lineH(9.5) +
      2 +
      maxLineasValor * lineH(11);

    asegurarEspacio(alturaCard + 10, MARGIN_Y);
    const yCardTop = y;
    page.drawRectangle({
      x: MARGIN_X,
      y: yCardTop - alturaCard,
      width: CONTENT_W,
      height: alturaCard,
      borderColor: BORDE_SUTIL,
      borderWidth: 1,
      color: FONDO_PAGINA,
    });

    let yc = yCardTop - cardPad.y - 9;
    for (const linea of lineasTitulo) {
      page.drawText(linea, { x: MARGIN_X + cardPad.x, y: yc, size: TAMANO_TITULO_MEDICAMENTO, font: f.serifBold, color: AZUL });
      yc -= lineH(TAMANO_TITULO_MEDICAMENTO);
    }
    for (const linea of lineasComercial) {
      page.drawText(linea, { x: MARGIN_X + cardPad.x, y: yc, size: 10, font: f.sans, color: GRIS_CLARO });
      yc -= lineH(10);
    }
    yc -= 6;

    columnas.forEach(([label], idx) => {
      const cx = MARGIN_X + cardPad.x + idx * (cardColW + cardGapCols);
      drawTracked(page, label, cx, yc, f.sans, 9.5, GRIS_OSCURO);
      let yValor = yc - lineH(9.5) - 2;
      for (const linea of lineasPorColumna[idx]!) {
        page.drawText(linea, { x: cx, y: yValor, size: 11, font: f.sans, color: TEXTO });
        yValor -= lineH(11);
      }
    });

    y = yCardTop - alturaCard - 10;
  });

  // Receta especial (sustancia controlada) — no forma parte del rediseño
  // (el mockup es de una receta común), se mantiene el aviso simple con la
  // tipografía nueva.
  if (datos.sustanciaControlada && datos.numeroSerieRecetaEspecial) {
    asegurarEspacio(40, MARGIN_Y);
    page.drawText(`Formulario ACESS N° ${datos.numeroSerieRecetaEspecial}`, { x: MARGIN_X, y, size: 12, font: f.sansBold, color: TEXTO });
    y -= lineH(12);
    parrafo('Sustancia controlada — la validez de esta receta depende del formulario físico pre-numerado de ACESS, no del código QR del pie.', MARGIN_X, CONTENT_W, {
      size: 9,
      color: GRIS_CLARO,
    });
    y -= 6;
  }

  // ---------------------------------------------------------------------
  // 5. INDICACIONES PARA EL PACIENTE (Resolución ACESS-2023-0030, Art. 5.e)
  // ---------------------------------------------------------------------
  if (datos.signosAlarma || datos.recomendacionesNoFarmacologicas) {
    asegurarEspacio(90, MARGIN_Y);
    page.drawText('Indicaciones para el paciente', { x: MARGIN_X, y, size: 15, font: f.serifBold, color: AZUL });
    y -= lineH(15) + 8;

    if (datos.signosAlarma) {
      const prefijo = 'Signos de alarma — ';
      const resto = `consulte a su médico o acuda a emergencias si presenta: ${datos.signosAlarma}`;
      const anchoDisponible = CONTENT_W - 12 - 12; // padding izq/der del callout
      const lineasResto = wrapText(prefijo + resto, f.sans, 12.5, anchoDisponible);
      const alturaCallout = lineasResto.length * lineH(12.5) + 18;
      asegurarEspacio(alturaCallout + 10, MARGIN_Y);
      const yCalloutTop = y;
      page.drawRectangle({ x: MARGIN_X, y: yCalloutTop - alturaCallout, width: CONTENT_W, height: alturaCallout, color: ROJO_FONDO });
      page.drawRectangle({ x: MARGIN_X, y: yCalloutTop - alturaCallout, width: 3, height: alturaCallout, color: ROJO_BORDE });
      let yCallout = yCalloutTop - 9 - 10;
      // Primera línea con "Signos de alarma" en bold rojo + el resto en el mismo color pero regular.
      const anchoPrefijo = anchoTexto('Signos de alarma', f.sansBold, 12.5);
      page.drawText('Signos de alarma', { x: MARGIN_X + 12, y: yCallout, size: 12.5, font: f.sansBold, color: ROJO_TEXTO });
      const primeraLineaResto = wrapText(`— ${resto}`, f.sans, 12.5, anchoDisponible - anchoPrefijo - 4)[0] ?? '';
      page.drawText(primeraLineaResto, { x: MARGIN_X + 12 + anchoPrefijo + 4, y: yCallout, size: 12.5, font: f.sans, color: ROJO_TEXTO });
      const restanteTrasPrimera = `— ${resto}`.slice(primeraLineaResto.length).trim();
      yCallout -= lineH(12.5);
      for (const linea of wrapText(restanteTrasPrimera, f.sans, 12.5, anchoDisponible)) {
        page.drawText(linea, { x: MARGIN_X + 12, y: yCallout, size: 12.5, font: f.sans, color: ROJO_TEXTO });
        yCallout -= lineH(12.5);
      }
      y = yCalloutTop - alturaCallout - 12;
    }

    if (datos.recomendacionesNoFarmacologicas) {
      asegurarEspacio(40, MARGIN_Y);
      const anchoEtiqueta = anchoTexto('Recomendaciones: ', f.sansBold, 12.5);
      page.drawText('Recomendaciones: ', { x: MARGIN_X, y, size: 12.5, font: f.sansBold, color: GRIS_OSCURO });
      const primeraLinea = wrapText(datos.recomendacionesNoFarmacologicas, f.sans, 12.5, CONTENT_W - anchoEtiqueta)[0] ?? '';
      page.drawText(primeraLinea, { x: MARGIN_X + anchoEtiqueta, y, size: 12.5, font: f.sans, color: TEXTO });
      y -= lineH(12.5);
      const restante = datos.recomendacionesNoFarmacologicas.slice(primeraLinea.length).trim();
      for (const linea of wrapText(restante, f.sans, 12.5, CONTENT_W)) {
        page.drawText(linea, { x: MARGIN_X, y, size: 12.5, font: f.sans, color: TEXTO });
        y -= lineH(12.5);
      }
    }
    y -= 8;
  }

  // ---------------------------------------------------------------------
  // 6. PIE DE PÁGINA — teléfono de contacto y una regla de cierre. Hasta
  // 2026-09-15 acá también iba el QR de "validación de dispensación"
  // (`datos.urlValidacion`, portal `/farmacia/:token`) — se sacó del PDF a
  // pedido de Sue; el token y el link siguen viajando en la respuesta de
  // /firmar-documento para "Envío digital" (WhatsApp/correo), solo dejaron
  // de imprimirse en el papel.
  //
  // SEGUNDO bug real, reportado por el usuario viendo el PDF real: el pie
  // estaba "empujado al fondo" (anclado a `MARGIN_Y` sin importar cuánto
  // contenido hubiera arriba) — en una página 2 con poco contenido (solo
  // Indicaciones) eso dejaba un hueco enorme e injustificado en el medio de
  // la página, nada que ver con el mockup. `margin-top:auto` tiene sentido
  // en el mockup porque ahí es SIEMPRE una sola página con harto contenido;
  // en un documento que pagina de verdad, la regla correcta es que el pie
  // fluya inmediatamente después del último contenido (como cualquier otra
  // sección), no que se estire hacia el borde inferior.
  // ---------------------------------------------------------------------
  const alturaFooter = (datos.practitionerTelefono ? lineH(10.5) + 3 : 0) + 1; // teléfono + regla

  // Fluye inmediatamente después del último contenido (con un gap fijo),
  // nunca anclado al margen inferior — ver el comentario de arriba sobre
  // el hueco que eso dejaba en una página con poco contenido.
  asegurarEspacio(alturaFooter + 14, MARGIN_Y);
  let yFooter = y - 14;
  if (datos.practitionerTelefono) {
    page.drawText(`Contacto del prescriptor: ${datos.practitionerTelefono}`, { x: MARGIN_X, y: yFooter, size: 10.5, font: f.sans, color: GRIS_CLARO });
    yFooter -= lineH(10.5) + 3;
  }
  page.drawLine({ start: { x: MARGIN_X, y: yFooter }, end: { x: PAGE_W - MARGIN_X, y: yFooter }, thickness: 1, color: BORDE_SUTIL });

  return doc.save();
}
